// Mystery Gift: keeps exactly one gift in the cart while the qualifying total reaches the threshold
// (Theme settings > Cart), and removes it below. The price is made free by the automatic discount
// "Mystery Gift" (visible in the cart and checkout). The gift may never be paid for: if the discount
// does not apply to this cart, the line is removed again and not re-added until the cart contents change.
(() => {
  const config = (() => {
    try {
      return JSON.parse(document.getElementById('cl-mystery-gift-config')?.textContent || '{}');
    } catch (error) {
      console.error('Mystery Gift settings could not be read', error);
      return {};
    }
  })();
  const giftId = Number(config.variant_id);
  const juoGiftId = Number(config.juo_variant_id) || null;
  if (!config.enabled || !giftId || !config.threshold) return;

  const root = () => window.Shopify?.routes?.root || '/';
  let busy = false;
  // Survives the reload on the cart page, so a gift that is not free is not added again in a loop.
  const blockedKey = 'cl-mystery-gift-blocked';
  let blockedSignature = (() => {
    try { return window.sessionStorage.getItem(blockedKey); } catch (error) { return null; }
  })();
  function block(value) {
    blockedSignature = value;
    try { window.sessionStorage.setItem(blockedKey, value); } catch (error) { console.warn('Mystery Gift: sessionStorage unavailable', error); }
  }

  // The cart contents that decide the discount (without the gift): variant, quantity and selling plan.
  function signature(cart) {
    return cart.items
      .filter((item) => item.variant_id !== giftId)
      .map((item) => `${item.variant_id}x${item.quantity}@${item.selling_plan_allocation?.selling_plan?.id || ''}`)
      .sort()
      .join('|');
  }

  // Same total as the progress bar: shipped lines after discounts, without the gift itself.
  function qualifyingTotal(cart) {
    const lines = cart.items
      .filter((item) => item.variant_id !== giftId && item.requires_shipping)
      .reduce((sum, item) => sum + item.final_line_price, 0);
    const cartDiscounts = (cart.cart_level_discount_applications || [])
      .reduce((sum, discount) => sum + discount.total_allocated_amount, 0);
    return lines - cartDiscounts;
  }

  function bundledSections() {
    const sections = new Set();
    document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } }));
    return [...sections];
  }

  async function post(path, body) {
    const sections = bundledSections();
    if (sections.length) body.sections = sections.join(',');
    const response = await fetch(`${root()}cart/${path}`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
    });
    const content = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(content.description || content.message || `Cart update failed (${response.status})`);
    return content;
  }

  async function freshSections(fallback) {
    const ids = bundledSections();
    if (!ids.length) return fallback;
    try {
      const response = await fetch(`${root()}?sections=${encodeURIComponent(ids.join(','))}&t=${Date.now()}`, { cache: 'no-store' });
      if (!response.ok) throw new Error(`sections ${response.status}`);
      return await response.json();
    } catch (error) {
      console.warn('Mystery Gift: could not load fresh cart sections', error);
      return fallback;
    }
  }

  async function sync(cart) {
    if (busy || !cart?.items) return;
    // Back from the JUO checkout: cloudless-cart.js first restores the plans and the gift, then triggers a new sync.
    if (cart.items.some((item) => item.properties?._selling_plan_id || item.variant_id === juoGiftId)) return;
    const giftLine = cart.items.find((item) => item.variant_id === giftId);
    const hasOtherProducts = cart.items.some((item) => item.variant_id !== giftId);
    const qualifies = hasOtherProducts && qualifyingTotal(cart) >= config.threshold;
    const giftNotFree = giftLine && giftLine.final_line_price > 0;
    if (giftNotFree) block(signature(cart));
    let change = null;
    if (giftNotFree) change = ['update.js', { updates: { [giftLine.key]: 0 } }];
    else if (qualifies && !giftLine && signature(cart) === blockedSignature) return;
    else if (qualifies && !giftLine) change = ['add.js', { items: [{ id: giftId, quantity: 1, properties: { _mystery_gift: 'true' } }] }];
    else if (!qualifies && giftLine) change = ['update.js', { updates: { [giftLine.key]: 0 } }];
    else if (giftLine && giftLine.quantity > 1) change = ['change.js', { id: giftLine.key, quantity: 1 }];
    if (!change) return;

    busy = true;
    try {
      const result = await post(...change);
      if (window.themeVariables?.settings?.pageType === 'cart') {
        window.location.reload();
        return;
      }
      // Let the change that triggered this sync finish rendering first, then send the final state with fresh
      // section HTML, so the drawer never keeps showing a gift that is no longer in the cart.
      await new Promise((resolve) => window.setTimeout(resolve, 250));
      const updated = await fetch(`${root()}cart.js?t=${Date.now()}`, { cache: 'no-store' }).then((response) => response.json());
      updated.sections = await freshSections(result.sections);
      const addedGift = updated.items?.find((item) => item.variant_id === giftId);
      if (addedGift && addedGift.final_line_price > 0) {
        // Discount did not apply (e.g. the threshold is reached with subscriptions only): never keep a paid gift.
        busy = false;
        await sync(updated);
        return;
      }
      document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
        bubbles: true, detail: { baseEvent: 'cloudless:mystery-gift', cart: updated },
      }));
    } catch (error) {
      console.error('Mystery Gift could not be updated', error);
    } finally {
      busy = false;
    }
  }

  document.addEventListener('cart:change', (event) => {
    if (event.detail?.baseEvent === 'cloudless:mystery-gift') return;
    const cart = event.detail?.cart;
    if (cart?.items) sync(cart);
    else fetch(`${root()}cart.js`).then((response) => response.json()).then(sync).catch((error) => console.error(error));
  });
  fetch(`${root()}cart.js`).then((response) => response.json()).then(sync).catch((error) => console.error(error));
})();
